import React, {
  useState,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { CompactPicker } from "react-color";
import { useFormikContext, useField } from "formik";
import styled, { useTheme } from "styled-components";
import { Flex, Box } from "reflexbox";
import Label from "../Label";
import { ShimmerField } from "../../Shimmer";
import { Body, Helper } from "../../Text";
import Button from "../../Button";

const Container = styled(Flex)`
  margin: 0.5rem auto 1rem auto !important;
  flex-direction: column;
  button {
    margin: 0 !important;
    svg {
      color: ${({ svgColor }) => svgColor} !important;
    }
  }
`;

const FlexContainer = styled(Flex)`
  flex-direction: column;
  width: 100%;
  position: relative;

  transition: all ease-in-out 200ms;

  opacity: ${({ disabled }) => (disabled ? "0.7" : "1")};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "auto")};

  &:last-of-type {
    margin-bottom: 0;
  }

  input {
    -webkit-appearance: none;
    appearance: none;
    padding: 1.25rem 0;
    border: none;
    border-radius: 0.25rem;
    transform-origin: center;
    ${({ theme }) => `
      background-color: ${theme.backgroundColor};
      color: ${theme.textDark};
    `}
  }
`;

const ColorPicker = forwardRef(
  (
    { name, labelText, description, isLoading, disabled, cta, required },
    ref
  ) => {
    const theme = useTheme();

    const [show, setShow] = useState(false);

    const { setFieldValue } = useFormikContext();
    const [field, meta] = useField(name);

    const toggle = useRef(null);

    const handleClickOutside = useCallback(
      (e) => {
        if (ref?.current && !ref.current.contains(e.target) && show) {
          setShow(false);
        }
      },
      [ref, show]
    );

    useEffect(() => {
      if (show) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [handleClickOutside, show]);

    return (
      <Container width={[1, 1, "calc(50% - 0.5rem)"]} svgColor={field.value}>
        {labelText && <Label htmlFor={name}>{labelText}</Label>}
        {description && <Helper>{description}</Helper>}
        {isLoading ? (
          <ShimmerField />
        ) : (
          <FlexContainer
            ref={toggle}
            alignItems="flex-start"
            disabled={disabled}
            cta={cta}
          >
            <Button
              mt="0.5rem"
              ml="0.5rem"
              reverse
              color={theme.gray}
              iconName="faPalette"
              onClick={() => setShow(!show)}
            >
              {field.value}
            </Button>
            <Box
              mt="0.5rem"
              style={{
                display: show ? "block" : "none",
              }}
            >
              <CompactPicker
                required={required}
                id={name}
                key={name}
                innerRef={ref}
                name={name}
                disabled={disabled}
                color={field.value || theme.primary}
                onChangeComplete={({ hex }) => setFieldValue(name, hex)}
              />
            </Box>
          </FlexContainer>
        )}
        {meta.error && (
          <Body my="0.5rem" color={theme.danger}>
            {meta.error}
          </Body>
        )}
      </Container>
    );
  }
);

export default ColorPicker;
